import { AxiosResponse } from 'axios';
import { MoldOrder, ActionType, MyTasks, OrderDetail } from '../../types/mold';
import {
  PatchMoldOrderRequest,
  PostMoldOrderRequest,
  MoldOrderSearchParams,
  MoldOrderSearchCsvParams,
  MoldOrderSearchResponse,
} from '../../types/moldOrder';
import { noteMappingOptions } from '../../constants/moldOrder';
import { MoldOrderStats } from '../../types/statistics';
import api from './client';

export const moldOrderService = {
  getMoldOrders: async (params?: {
    moldId?: number;
    activeOrders?: boolean;
    includeWorkflowMeta?: boolean;
  }): Promise<AxiosResponse<MoldOrder[]>> => {
    return api.get('/mold-orders', {
      params,
    });
  },

  // Server-side paginated/filtered/sorted search (list page v2, §5.2 of the mold-order-list-v2
  // plan). `getMoldOrders` above stays untouched — kanban/prioritizer/mold-detail/type-explorer
  // still use it. `indexes: null` makes axios serialize array params as repeat-key
  // (statuses=a&statuses=b) rather than the default statuses[]=a&statuses[]=b, matching the
  // gin/oapi-codegen "form" style binding used server-side (assumption A3, confirmed).
  searchMoldOrders: async (
    params: MoldOrderSearchParams,
  ): Promise<AxiosResponse<MoldOrderSearchResponse>> => {
    return api.get('/mold-orders/search', {
      params,
      paramsSerializer: { indexes: null },
    });
  },

  // Server-generated CSV of the current filtered result set (decision #2 — ignores
  // limit/offset). Auth here is a Bearer header, not a cookie (see client.ts), so a bare
  // <a href>/window.location navigation to this URL would 401; go through axios with
  // responseType:'blob' instead (same pattern as orderExportService.downloadCsv) and let the
  // caller turn the blob into a download via a temporary object URL.
  searchMoldOrdersCsv: async (
    params: MoldOrderSearchCsvParams,
  ): Promise<AxiosResponse<Blob>> => {
    return api.get('/mold-orders/search.csv', {
      params,
      paramsSerializer: { indexes: null },
      responseType: 'blob',
    });
  },

  getMoldOrderById: async (
    id: number,
    withWorkflowStructure: boolean = false,
  ): Promise<AxiosResponse<MoldOrder>> => {
    return api.get(`/mold-orders/${id}`, {
      params: { withWorkflowStructure },
    });
  },

  getMoldOrderStats: async (): Promise<AxiosResponse<MoldOrderStats>> => {
    return api.get('/mold-orders/stats');
  },

  createMoldOrder: async (
    data: PostMoldOrderRequest,
  ): Promise<AxiosResponse<MoldOrder>> => {
    return api.post('/mold-orders', data);
  },

  updateMoldOrder: async (
    id: number,
    data: PatchMoldOrderRequest,
  ): Promise<AxiosResponse<void>> => {
    return api.patch(`/mold-orders/${id}`, data);
  },

  deleteMoldOrder: async (id: number): Promise<AxiosResponse<void>> => {
    return api.delete(`/mold-orders/${id}`);
  },

  submitAction: async (
    id: number,
    data: {
      action: ActionType;
      orderDetail?: Record<string, any>;
      assignedTo: number;
    },
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/mold-orders/${id}`, data);
  },

  saveOrderDetail: async (
    id: number,
    formData: Record<string, any>,
    formVersion: string,
    // Optional extra order_detail siblings merged top-level (NOT inside `form`), e.g.
    // resolvedSnapshot stamped at finalize. The backend MergeNewOrderDetail merges any
    // top-level key, so these persist alongside `form` without polluting the answers.
    extra?: Record<string, any>,
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/mold-orders/${id}/order-detail`, {
      form: formData,
      formVersion,
      ...extra,
    });
  },

  updateOrderDetail: async (
    id: number,
    orderDetail: Partial<OrderDetail>,
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/mold-orders/${id}/order-detail`, orderDetail);
  },

  updateMoldOrderStateInstance: async (
    instanceId: number,
    data: { notes?: string },
  ): Promise<AxiosResponse<void>> => {
    return api.patch(`/order-state-instances/${instanceId}`, data);
  },

  downloadPdfReport: async (id: number): Promise<AxiosResponse<Blob>> => {
    return api.get(`/mold-orders/${id}/report`, {
      responseType: 'blob',
    });
  },

  getNotesOptions: async (): Promise<{ value: string; label: string }[]> => {
    return noteMappingOptions();
  },

  submitApproval: async (
    orderId: number,
    data: {
      stateInstanceId: number;
      approverInstanceId: number;
      action: 'approve' | 'reject' | 'rework';
      comments?: string;
    },
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/mold-orders/${orderId}/approval`, data);
  },

  getMyTasks: async (): Promise<AxiosResponse<{ myWorks: MyTasks }>> => {
    return api.get('/mold-orders/myworks');
  },
};
